import React from "react";
import {
  Box,
  Text,
  Group,
  Transition,
  Badge,
  useMantineTheme,
  Stack,
} from "@mantine/core";
import { Event, EventTeam } from "@/utils/api";
import {
  formatDaysOfWeek,
  formatUTCTimeRange,
  apiTimeToFormTime,
} from "@/utils";
import { DEFAULT_TIME_FORMAT } from "@/config";

export interface DisplayBlockScheduleEventRowProps {
  displayMainRoom?: boolean;
  displayFloor?: boolean;
  event: Event;
  mounted?: boolean;
  className?: string;
  themeConfig?: any;
}

interface EventStatus {
  type: "ongoing" | "upcoming" | "ending" | "ended" | "future" | "canceled";
  label: string;
  cssClass: string;
}

export const DisplayBlockScheduleEventRow: React.FC<
  DisplayBlockScheduleEventRowProps
> = ({ event, mounted = true, className, displayMainRoom, themeConfig }) => {
  const theme = useMantineTheme();

  const getEventRowTheme = () => {
    const themeConfig = (theme as any).other?.eventRowTheme;

    if (!themeConfig) {
      throw new Error(
        "eventRowTheme configuration is required in theme.other.eventRowTheme",
      );
    }

    return themeConfig;
  };

  const eventRowTheme = getEventRowTheme();

  /**
   * Parse time string to minutes since midnight
   * Uses apiTimeToFormTime helper for consistent parsing
   */
  const parseTimeToMinutes = (
    timeString: string | undefined,
  ): number | null => {
    if (!timeString) return null;

    try {
      // Convert API time to HH:mm format
      const formTime = apiTimeToFormTime(timeString);

      if (!formTime) return null;

      // Parse HH:mm to get hours and minutes
      const [hours, minutes] = formTime.split(":").map(Number);

      if (isNaN(hours) || isNaN(minutes)) return null;

      return hours * 60 + minutes;
    } catch {
      return null;
    }
  };

  const getEventStatus = (event: Event): EventStatus => {
    // Check for canceled status first - regardless of date/time
    if (event.status === 50) {
      return {
        type: "canceled",
        label: "CANCELLED",
        cssClass: "display-block-schedule-event-row--canceled",
      };
    }

    // If no time information, treat as future event
    if (!event.startTime || !event.endTime) {
      return {
        type: "future",
        label: "",
        cssClass: "display-block-schedule-event-row--future",
      };
    }

    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());

    // Parse event date if available
    let eventDate: Date | null = null;
    if (event.eventDate) {
      try {
        const [year, month, day] = event.eventDate.split("-").map(Number);
        eventDate = new Date(year, month - 1, day);

        // Validate the parsed date
        if (isNaN(eventDate.getTime())) {
          eventDate = null;
        }
      } catch {
        eventDate = null;
      }
    }

    // If event has a valid date and it's not today, check if it's in the past or future
    if (eventDate) {
      if (eventDate < today) {
        return {
          type: "ended",
          label: "ENDED",
          cssClass: "display-block-schedule-event-row--ended",
        };
      }

      // If event is in the future (not today), return future status
      if (eventDate > today) {
        return {
          type: "future",
          label: "",
          cssClass: "display-block-schedule-event-row--future",
        };
      }
    }

    // Event is today (or no date specified), proceed with time-based logic
    const currentTime = now.getHours() * 60 + now.getMinutes();

    // Parse start and end times using helper
    const startTime = parseTimeToMinutes(event.startTime);
    const endTime = parseTimeToMinutes(event.endTime);

    // If we can't parse times, default to future
    if (startTime === null || endTime === null) {
      return {
        type: "future",
        label: "",
        cssClass: "display-block-schedule-event-row--future",
      };
    }

    const timeUntilStart = startTime - currentTime;
    const timeUntilEnd = endTime - currentTime;

    // Event has ended
    if (currentTime > endTime) {
      return {
        type: "ended",
        label: "ENDED",
        cssClass: "display-block-schedule-event-row--ended",
      };
    }

    // Event is currently happening
    if (currentTime >= startTime && currentTime <= endTime) {
      // Within the last 15 minutes, including the final minute itself
      // (timeUntilEnd === 0, i.e. currentTime === endTime). `timeUntilEnd` is
      // always >= 0 here because the enclosing check guarantees
      // currentTime <= endTime, and anything past the end is handled above as
      // "ENDED", so we must not exclude 0 or the last minute wrongly flips to
      // "HAPPENING NOW".
      if (timeUntilEnd <= 15 && timeUntilEnd >= 0) {
        return {
          type: "ending",
          label: "ENDS SOON",
          cssClass: "display-block-schedule-event-row--ending",
        };
      }
      return {
        type: "ongoing",
        label: "HAPPENING NOW",
        cssClass: "display-block-schedule-event-row--ongoing",
      };
    }

    // Event is starting soon
    if (timeUntilStart <= 15 && timeUntilStart > 0) {
      return {
        type: "upcoming",
        label: "STARTING SOON",
        cssClass: "display-block-schedule-event-row--upcoming",
      };
    }

    // Event hasn't started yet
    return {
      type: "future",
      label: "",
      cssClass: "display-block-schedule-event-row--future",
    };
  };

  const renderTeamsWithRooms = (event: Event) => {
    if (!event.eventTeams || event.eventTeams.length === 0) {
      return null;
    }

    return event.eventTeams.map((eventTeam: EventTeam, index: number) => {
      const supportingRooms = eventTeam.supportingRooms
        ?.map((room) => room.roomNumber || room.name)
        .join(", ");

      return (
        <Group
          key={index}
          gap={theme.spacing.sm}
          wrap="nowrap"
          align="baseline"
        >
          <Text
            style={Object.assign(
              {},
              statusTheme.teamName,
              themeConfig?.events?.teams,
            )}
          >
            {eventTeam.teamName}{" "}
            {!!supportingRooms && <> - [{supportingRooms}]</>}
          </Text>
        </Group>
      );
    });
  };

  const renderMainRoomInfo = (event: Event) => {
    if (!displayMainRoom) return null;

    let roomName = event.room?.roomNumber || event.room?.name;
    let location = event.room?.floor;
    let roomLabel = "ROOM";
    let locationLabel = "LOCATION";

    if (Array.isArray(event.rooms) && event.rooms.length) {
      roomLabel = event.rooms.length > 1 ? "ROOMS" : roomLabel;
      locationLabel = event.rooms.length > 1 ? "LOCATIONS" : locationLabel;

      roomName = event.rooms
        .map((room) => room.name || room.roomNumber)
        .join(", ");
      location = Array.from(
        new Set(event.rooms.map((room) => room.floor)),
      ).join(", ");
    }

    if (!roomName && !location) return null;

    return (
      <Stack gap={"xs"}>
        {roomName && (
          <Badge
            p={"sm"}
            style={Object.assign(
              {},
              statusTheme.details,
              themeConfig?.events?.badges?.room,
            )}
          >
            {roomLabel}:&nbsp;{roomName}
          </Badge>
        )}
        {location && (
          <Badge
            p={"sm"}
            style={Object.assign(
              {},
              statusTheme.details,
              themeConfig?.events?.badges?.location,
            )}
          >
            {locationLabel}:&nbsp;{location}
          </Badge>
        )}
      </Stack>
    );
  };

  const status = getEventStatus(event);

  const containerTheme = themeConfig?.events.container || {};
  const statusThemeCustom =
    themeConfig?.events.states[status.type] || containerTheme;
  const statusTheme = eventRowTheme[status.type];

  const teamsWithRooms = renderTeamsWithRooms(event);
  const mainRoomInfo = renderMainRoomInfo(event);

  return (
    <Transition mounted={mounted} transition="slide-up" duration={300}>
      {(styles) => (
        <Box
          className={`display-block-schedule-event-row ${status.cssClass} ${
            className || ""
          }`}
          style={{
            ...styles,
            ...statusTheme.container,
            ...containerTheme,
            borderLeftColor: statusThemeCustom.background,
          }}
        >
          {/* Row 1: Time Badge + Event Name */}
          <Group gap={theme.spacing.md} align="center" wrap="nowrap" mb={0}>
            {/* Time Badge */}
            <Box
              style={Object.assign(
                {},
                statusTheme.timeBadge,
                themeConfig?.events?.badges?.timeRange || {},
              )}
            >
              <Text style={statusTheme.timeBadgeText}>
                {formatUTCTimeRange(
                  event.startTime,
                  event.endTime,
                  DEFAULT_TIME_FORMAT,
                  true,
                )}
              </Text>
            </Box>

            {/* Event Name */}
            <Text
              style={Object.assign(
                {},
                statusTheme.eventTitle,
                themeConfig?.events?.title || {},
              )}
            >
              {event.name}
            </Text>
          </Group>

          {/* Row 2: Status Badge (centered under time) + Event Details */}
          <Group gap={theme.spacing.md} align="flex-start" wrap="nowrap">
            {/* Status Badge Container */}
            <Stack
              mt={"xs"}
              gap={"xs"}
              style={{
                width: statusTheme.timeBadge?.width,
                maxWidth: statusTheme.timeBadge?.maxWidth,
                display: "flex",
                justifyContent: "center",
              }}
            >
              {event.recurrencePattern && event.daysOfWeek && (
                <Badge
                  style={Object.assign(
                    {},
                    statusTheme.recurrenceBadge,
                    themeConfig?.events?.badges?.recurrence,
                  )}
                >
                  Every:{" "}
                  {formatDaysOfWeek(event.daysOfWeek, event.recurrencePattern)}
                </Badge>
              )}

              {status.label && (
                <Badge
                  variant="filled"
                  style={Object.assign(
                    {},
                    statusTheme.statusBadge,
                    statusThemeCustom,
                  )}
                >
                  {status.label}
                </Badge>
              )}
            </Stack>

            {/* Event Details */}
            <Box style={{ flex: 1 }}>
              {mainRoomInfo}
              {teamsWithRooms && (
                <Stack mt={"xs"} gap={0}>
                  {teamsWithRooms}
                </Stack>
              )}
            </Box>
          </Group>
        </Box>
      )}
    </Transition>
  );
};
